import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { not } from 'ember-truth-helpers';
import CloseButton from 'garaje/components/close-button';
import LineClamp from 'garaje/components/line-clamp';
import ButtonGroup from 'garaje/components/ui/button-group';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type FlashMessagesService from 'garaje/services/flash-messages';
import urlBuilder from 'garaje/utils/url-builder';

interface ProfileNotesSignature {
  Args: {
    visitorProfileId: string;
  };
  Element: HTMLDivElement;
}

interface NoteResponse {
  data: {
    attributes: {
      note: string;
    };
  };
}

const MAX_NOTE_LENGTH = 1000;

// Inline SVG — Figma node 6171:44605 (folded-corner notepad icon)
const NotepadIcon = <template>
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" ...attributes>
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M0 0V16H9.26316V9.26316H16V0H0ZM10.9474 16L16 10.9474H10.9474V16Z"
      fill="#8A8F9E"
    />
  </svg>
</template>;

export default class ProfileNotes extends Component<ProfileNotesSignature> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare flashMessages: FlashMessagesService;

  @tracked noteText = '';
  @tracked draftText = '';
  @tracked isModalOpen = false;

  loadNoteTask = restartableTask(async () => {
    try {
      const url = urlBuilder.v3.visitorProfiles.note(this.args.visitorProfileId);
      const response = await this.ajaxFetch.request<NoteResponse>(url);
      this.noteText = response?.data?.attributes?.note ?? '';
    } catch {
      this.noteText = '';
    }
  });

  saveNoteTask = dropTask(async () => {
    const textToSave = this.draftText;
    try {
      const url = urlBuilder.v3.visitorProfiles.note(this.args.visitorProfileId);
      await this.ajaxFetch.put(url, {
        contentType: 'application/json',
        data: JSON.stringify({
          data: {
            attributes: {
              note: textToSave,
            },
          },
        }),
      });
      this.noteText = textToSave;
      this.isModalOpen = false;
      this.flashMessages.showAndHideFlash('success', 'Note saved');
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Failed to save note', { showAboveModalOverlay: true });
    }
  });

  get charsRemaining(): number {
    return MAX_NOTE_LENGTH - this.draftText.length;
  }

  get isOverLimit(): boolean {
    return this.draftText.length > MAX_NOTE_LENGTH;
  }

  get hasChanges(): boolean {
    return this.draftText !== this.noteText;
  }

  get canSave(): boolean {
    return this.hasChanges && !this.isOverLimit && !this.saveNoteTask.isRunning;
  }

  openModal = (): void => {
    if (this.loadNoteTask.isRunning) return;
    this.draftText = this.noteText;
    this.isModalOpen = true;
  };

  closeModal = (): void => {
    this.isModalOpen = false;
  };

  reloadNote = (): void => {
    void this.saveNoteTask.cancelAll();
    this.noteText = '';
    this.isModalOpen = false;
    void this.loadNoteTask.perform();
  };

  updateDraft = (event: Event): void => {
    this.draftText = (event.target as HTMLTextAreaElement).value;
  };

  constructor(owner: unknown, args: ProfileNotesSignature['Args']) {
    super(owner, args);
    void this.loadNoteTask.perform();
  }

  <template>
    <div data-test-profile-notes {{didUpdate this.reloadNote @visitorProfileId}} ...attributes>
      {{! Display area — clickable to open modal }}
      {{#if (can "manage-note visitor-profile")}}
        {{#if this.loadNoteTask.isRunning}}
          <div class="rounded px-5 py-2.5 h-[139px] w-full" data-test-notes-loading>
            <div class="animate-pulse">
              <div class="h-4 bg-carbon-10 rounded w-24 mb-2"></div>
              <div class="h-3 bg-carbon-10 rounded w-3/4"></div>
            </div>
          </div>
        {{else if this.noteText}}
          {{! Has note — plain text by default, gray card on hover }}
          <button
            type="button"
            data-test-notes-container
            class="rounded px-5 py-2.5 cursor-pointer relative group h-[139px] w-full text-left flex flex-col items-start gap-2.5 hover:bg-carbon-5 transition-colors duration-200"
            {{on "click" this.openModal}}
          >
            <EmberTooltip @side="top" @text="View / edit internal notes" @tooltipClass="profile-notes-tooltip" />
            <div class="flex items-center gap-2">
              <NotepadIcon />
              <span class="text-sm font-semibold text-carbon-70">Internal notes</span>
            </div>
            <LineClamp
              data-test-notes-display
              class="text-sm leading-5 text-carbon-60 break-all"
              @lineHeight={{1.25}}
              @lines={{4}}
              @text={{this.noteText}}
            />
          </button>
        {{else}}
          {{! Empty state — white bordered box per Figma 6170:77662 }}
          <button
            type="button"
            data-test-notes-container
            class="bg-white rounded-md border border-carbon-20 px-5 py-2.5 cursor-pointer relative group h-[139px] w-full text-left flex flex-col items-start gap-2.5 hover:bg-carbon-5 transition-colors duration-200"
            {{on "click" this.openModal}}
          >
            <EmberTooltip @side="top" @text="Add internal notes" @tooltipClass="profile-notes-tooltip" />
            <div class="flex items-center gap-2">
              <NotepadIcon />
              <span
                class="text-sm font-semibold text-carbon-40 group-hover:text-carbon-70 transition-colors duration-200"
              >Internal notes</span>
            </div>
            <p data-test-notes-display class="text-sm leading-5 text-carbon-40">
              Add notes for your team about this visitor
            </p>
          </button>
        {{/if}}
      {{else}}
        {{! Read-only view for users without manage permission }}
        {{#if this.noteText}}
          <div
            data-test-notes-container
            class="bg-carbon-5 rounded px-5 py-2.5 h-[139px] flex flex-col items-start gap-2.5"
          >
            <div class="flex items-center gap-2">
              <NotepadIcon />
              <span class="text-sm font-semibold text-carbon-70">Internal notes</span>
            </div>
            <LineClamp
              data-test-notes-display
              class="text-sm leading-5 text-carbon-60 break-all"
              @lineHeight={{1.25}}
              @lines={{4}}
              @text={{this.noteText}}
            />
          </div>
        {{/if}}
      {{/if}}

      {{! Edit modal }}
      {{#if this.isModalOpen}}
        <ModalDialog
          @clickOutsideToClose={{false}}
          @translucentOverlay={{true}}
          @onClose={{this.closeModal}}
          @targetAttachment="center"
          @containerClass="ember-modal-dialog !max-w-[600px]"
          @overlayClass="ember-modal-overlay--fadeIn"
        >
          <div class="p-8" data-test-notes-modal>
            <div class="flex items-start justify-between mb-5">
              <h2 class="text-2xl font-semibold text-carbon-70">Internal notes</h2>
              <CloseButton class="relative right-0 top-0" data-test-notes-modal-close @onClick={{this.closeModal}} />
            </div>

            <textarea
              data-test-notes-textarea
              class="w-full border border-carbon-20 rounded-md p-3 text-sm resize-vertical min-h-[200px]"
              maxlength={{MAX_NOTE_LENGTH}}
              placeholder="Add notes for your team about this visitor"
              value={{this.draftText}}
              {{on "input" this.updateDraft}}
            ></textarea>

            <div class="flex justify-end mt-2">
              <span data-test-char-count class="text-sm text-carbon-40">Characters left: {{this.charsRemaining}}</span>
            </div>

            <ButtonGroup class="flex justify-end mt-4" @reverse={{true}}>
              <button
                type="button"
                class="btn btn--small btn--brand"
                disabled={{not this.canSave}}
                data-test-notes-save
                {{on "click" (perform this.saveNoteTask)}}
              >
                {{if this.saveNoteTask.isRunning "Saving..." "Save"}}
              </button>
              <button
                type="button"
                class="btn btn--small btn--outline"
                data-test-notes-cancel
                {{on "click" this.closeModal}}
              >
                Cancel
              </button>
            </ButtonGroup>
          </div>
        </ModalDialog>
      {{/if}}
    </div>
  </template>
}
